import { useState, useEffect, useCallback, type RefObject } from "react";

type OverflowEdge = "none" | "top" | "bottom" | "both";

export function useOverflow(ref: RefObject<HTMLElement | null>): OverflowEdge {
  const [edge, setEdge] = useState<OverflowEdge>("none");

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const canScroll = el.scrollHeight > el.clientHeight + 1;
    if (!canScroll) {
      setEdge("none");
      return;
    }
    const atTop = el.scrollTop <= 0;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
    if (!atTop && !atBottom) setEdge("both");
    else if (!atTop) setEdge("top");
    else if (!atBottom) setEdge("bottom");
    else setEdge("none");
  }, [ref]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    measure();

    el.addEventListener("scroll", measure, { passive: true });
    window.addEventListener("resize", measure);

    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(el);

    return () => {
      el.removeEventListener("scroll", measure);
      window.removeEventListener("resize", measure);
      resizeObserver.disconnect();
    };
  }, [ref, measure]);

  return edge;
}
